import { useState } from 'react'

import { Button, DialogFooter, LoadingButton } from '@fortem-monorepo/react-ui'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

import { useAccruedInterestsContext } from '../AccruedInterests.context'
import { BalanceCorrectionForm } from '../forms/BalanceCorrectionForm'
import { BalanceCorrectionFormFields } from '../forms/BalanceCorrectionFormFields'
import type { BalanceCorrectionValues } from '../forms/correction.schema'
import { AccruedInterestDialogFrame } from './AccruedInterestDialogFrame'
import { CorrectionReviewTable } from './CorrectionReviewTable'

export type BalanceCorrectionDialogProps = {
  params: AccruedInterests.Params
  row: AccruedInterests.BalanceRow
  onClose: () => void
}

export const BalanceCorrectionDialog = ({ params, row, onClose }: BalanceCorrectionDialogProps) => {
  const { stage } = useAccruedInterestsContext()
  const [review, setReview] = useState<BalanceCorrectionValues | null>(null)
  return (
    <AccruedInterestDialogFrame
      title={review ? 'Review balance correction' : 'Correct accrued interest balance'}
      description={`${params.fund_name} · ${params.value_date} · ${row.trade_id} · ${row.currency ?? '—'}`}
      onClose={onClose}
    >
      <p className="text-sm text-muted-foreground">
        {row.unique_identifier}. This correction stays pending until you confirm displayed balances.
      </p>
      <BalanceCorrectionForm
        defaultValues={{
          opening_balance_local: row.opening_balance_local ?? '',
          accrued_balance_local: row.accrued_balance_local ?? '',
          notes: row.notes ?? '',
        }}
        onSubmit={(values) => {
          if (!review) setReview({ ...values })
        }}
      >
        {({ formState }, submit) => (
          <>
            <div hidden={!!review}>
              <BalanceCorrectionFormFields disabled={!!review} />
            </div>
            {review && (
              <CorrectionReviewTable
                rows={[
                  ['Opening balance', row.opening_balance_local, review.opening_balance_local],
                  ['Closing balance', row.accrued_balance_local, review.accrued_balance_local],
                  ['Notes', row.notes, review.notes],
                ]}
              />
            )}
            <DialogFooter className="mt-4">
              <Button type="button" size="sm" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {review ? (
                <>
                  <Button type="button" size="sm" variant="outline" onClick={() => setReview(null)}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      stage(params, row, review)
                      onClose()
                    }}
                  >
                    Keep pending correction
                  </Button>
                </>
              ) : (
                <LoadingButton
                  type="submit"
                  size="sm"
                  disabled={!formState.isValid || formState.isSubmitting}
                  onClick={async (event) => {
                    event.preventDefault()
                    await submit()
                  }}
                >
                  Review correction
                </LoadingButton>
              )}
            </DialogFooter>
          </>
        )}
      </BalanceCorrectionForm>
    </AccruedInterestDialogFrame>
  )
}
